import { useCallback, useState } from "react";
import { IUsePersistState, PersistStateUpdater } from "./types";

const twoHours = 1000 * 60 * 60 * 1;

export const clearPersistedState = (storageKey: string) => {
  localStorage.removeItem(storageKey);
  localStorage.removeItem(`${storageKey}-dt`);
};

const usePersistState: IUsePersistState = <T>(storageKey: string, initialState: T) => {
  const [internalState, setInternalState] = useState<T>(() => {
    try {
      const inLocalStorage = localStorage.getItem(storageKey);
      if (inLocalStorage) {
        const lastUpdated = localStorage.getItem(`${storageKey}-dt`);
        if (lastUpdated && Date.now() - parseInt(lastUpdated) > twoHours) {
          clearPersistedState(storageKey);
          return initialState;
        }
        const retreivedState = JSON.parse(inLocalStorage);
        if (retreivedState) {
          return retreivedState as T;
        }
      }
    } catch {
      console.error(`Could not parse state from localStorage for key: ${storageKey}`);
    }
    return initialState;
  });

  const setState = useCallback(
    (newState: PersistStateUpdater<T>) => {
      setInternalState(prevState => {
        const resolvedState = typeof newState === "function" ? (newState as (prev: T) => T)(prevState) : newState;

        localStorage.setItem(storageKey, JSON.stringify(resolvedState));
        localStorage.setItem(`${storageKey}-dt`, Date.now().toString());

        return resolvedState;
      });
    },
    [storageKey],
  );

  return [internalState, setState];
};

export default usePersistState;
